import _ from 'lodash'
import { distance } from 'fastest-levenshtein'

type Token = {
	value: string
	quoted: boolean
}

/**
 * Returns true if all tokens in the query are found in the content
 * @param content - a string or array of strings to search in
 * @param query - a string or an array of strings in which the query will be searched
 * @param allowPartialMatch - default true - will allow partial matches (i.e. token "test" will match "testing", this can also be enabled by using * anywhere in the query)
 * @returns
 */
export const isMatch = (
	content: string | string[],
	query: string,
	allowPartialMatch: boolean = true
): boolean => {
	if (_.isEmpty(query)) {
		return true
	}

	allowPartialMatch = allowPartialMatch || query.includes('*')
	const queryTokens = processQuery(query)
	const contentArray = _.castArray(content)
	const contentTokens = contentArray.flatMap((item) =>
		tokenize(asciiFold(item))
	)

	return queryTokens.every((qToken) => {
		const strictMatch = contentTokens.includes(qToken.value)
		if (strictMatch) {
			return true
		}

		if (allowPartialMatch) {
			const partialMatch = contentTokens.some((cToken) =>
				cToken.includes(qToken.value)
			)
			if (partialMatch) {
				return true
			}
		}

		// quoted tokens skip fuzzy matching but still benefit from partial matching
		if (qToken.quoted) {
			return false
		}

		// check for distance match (allowing for minor typos)
		// allow 1 alteration every 4 characters
		const allowedDistance = Math.floor(qToken.value.length * 0.25)

		// when the token is a mac adresse we dont want to do distance matching
		// as it would lead to too many false positives
		if (allowedDistance > 0 && !isMacAddress(qToken.value)) {
			for (const cToken of contentTokens) {
				if (cToken.length < 3) {
					continue
				}

				// skip tokens that are too different in length
				if (
					Math.abs(cToken.length - qToken.value.length) >
					allowedDistance
				) {
					continue
				}

				const levDistance = distance(qToken.value, cToken)
				if (levDistance <= allowedDistance) {
					return true
				}
			}
		}

		return false
	})
}

const isMacAddress = _.memoize((str: string): boolean => {
	const macRegex = /^([0-9A-Fa-f:-]+)$/
	return macRegex.test(str)
})

const tokenize = (str: string): string[] => {
	// we allow the ":" character to allow searching for mac addresses and not split them
	// we allow the "&" character to allow searching for "ba&sh" and not split it
	return str?.split(/[^a-z0-9:&]/g).filter((t) => t.length > 0) ?? []
}

// used only to tokenize query, since the same query will be use
// on a multitude of records when filtering big lists
// this will return tokens with their quoted status to handle strict matching
// if there is an unmatched double quote, all the token after this quote will be considered as strict
const processQuery = _.memoize((str: string): Token[] => {
	const tokens: Token[] = []
	asciiFold(str)
		.split('"')
		.forEach((token, index) => {
			// even token are unquoted
			const quoted = index % 2 !== 0

			// if the original string starts/ends with a double quote, the split will generate empty tokens
			if (_.isEmpty(token)) {
				return
			}

			// we can have multiple terms inside quotes => "soho londo" rooftop
			tokenize(token).forEach((value) => {
				tokens.push({ value, quoted })
			})
		})

	return tokens
})

export const asciiFold = (str: string): string => {
	return str
		?.trim()
		.normalize('NFD') // Decompose accented chars
		.replace(/[\u0300-\u036f]/g, '') // Remove diacritics
		.toLowerCase() // Case fold
		.replace(/æ/g, 'ae') // Common ligatures
		.replace(/œ/g, 'oe')
		.replace(/ß/g, 'ss')
		.replace(/đ/g, 'd')
		.replace(/ø/g, 'o')
		.replace(/ł/g, 'l')
}
